import { Link } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";

const CookiePolicyPage = () => {
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-ink-50" data-testid="cookie-policy-page">
      <div className="bg-white border-b border-ink-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-950 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to XEA
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-coral-500 flex items-center justify-center">
            <Cookie className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-ink-950">Cookie Policy</h1>
            <p className="text-ink-500">Last updated: {currentDate}</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <div className="space-y-8 text-ink-700 leading-relaxed">

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">1. What Are Cookies?</h2>
              <p>
                Cookies are small text files stored on your device by your
                browser. Similar technologies — local storage, session storage,
                IndexedDB and pixel tags — work the same way for the purposes of
                this policy. We treat them all under the umbrella term
                &quot;cookies&quot; below.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">2. The Cookies We Use</h2>

              <p className="font-semibold text-ink-950 mt-4">a. Strictly Necessary (always on)</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Authentication token</strong> (localStorage) — keeps
                  you logged in between visits.
                </li>
                <li>
                  <strong>Cookie banner consent</strong> (localStorage) —
                  remembers your choice so the banner doesn&apos;t reappear.
                </li>
                <li>
                  <strong>Theme preference</strong> (localStorage) — remembers
                  light/dark mode.
                </li>
                <li>
                  <strong>Session id</strong> (cookie/localStorage) — used for
                  CSRF protection and A/B-test variant stability within a
                  session.
                </li>
              </ul>

              <p className="font-semibold text-ink-950 mt-4">b. Functional</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Email-reminder preference</strong> (server-side
                  setting linked to your account) — stores your chosen daily
                  email time. (Set in Settings → Daily Email Reminder.)
                </li>
                <li>
                  <strong>Community filter state</strong> (sessionStorage) —
                  remembers your selected filter (All / Images / Videos /
                  Following) within a session.
                </li>
              </ul>

              <p className="font-semibold text-ink-950 mt-4">c. Analytics &amp; A/B Testing (anonymous)</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Anonymous session id</strong> for our own A/B-test and
                  conversion-funnel analytics. We do not link this to your
                  email, name, or identity until you create an account, and we
                  never share it with third-party ad networks.
                </li>
                <li>
                  <strong>Performance counters</strong> (in-memory cache busts,
                  page-view counts) — aggregated only.
                </li>
              </ul>

              <p className="font-semibold text-ink-950 mt-4">d. Third-Party Cookies</p>
              <p>
                When you complete a payment, PayPal and our crypto-payment
                provider may set their own cookies on their own domains as part
                of their checkout flow. We do not control those cookies; please
                consult those providers&apos; cookie policies. We do not embed
                Google Ads, Meta Pixel, TikTok Pixel, or similar advertising
                trackers on the XEA site.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">3. We Do <em>Not</em> Use</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Advertising or remarketing cookies</li>
                <li>Cross-site tracking pixels</li>
                <li>Fingerprinting libraries beyond standard fraud-prevention</li>
                <li>
                  Browser push-notification subscriptions — the push system has
                  been retired. We send reminders by <strong>email only</strong>,
                  on a schedule you control.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">4. Managing Your Choices</h2>
              <p>
                When you first visit XEA you can choose &quot;Accept all&quot; or
                &quot;Essential only&quot;. Your choice is remembered for 12 months. You
                can change it at any time by clearing your browser&apos;s storage
                for our domain, then reloading the page — the consent banner
                will reappear.
              </p>
              <p className="mt-2">
                Most browsers allow you to block or delete cookies entirely
                through their settings, although doing so may break essential
                features like staying logged in.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">5. Changes</h2>
              <p>
                If we add new cookies or new third-party providers, we will
                update this page and (where required) request fresh consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-ink-950 mb-3">6. Contact</h2>
              <p>
                Questions about cookies?{" "}
                <a href="mailto:privacy@xe-a.com" className="text-coral-500 underline">
                  privacy@xe-a.com
                </a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
